/**
 * Cache Management Utilities
 * Provides functions to interact with service worker for cache management
 */

/**
 * Check if service worker is available
 */
function isServiceWorkerAvailable() {
  return 'serviceWorker' in navigator && navigator.serviceWorker.controller
}

/**
 * Send message to service worker and wait for response
 */
function sendMessageToSW(message) {
  return new Promise((resolve, reject) => {
    if (!isServiceWorkerAvailable()) {
      reject(new Error('Service worker not available'))
      return
    }

    const messageChannel = new MessageChannel()
    
    messageChannel.port1.onmessage = (event) => {
      resolve(event.data)
    }

    navigator.serviceWorker.controller.postMessage(message, [messageChannel.port2])
    
    // Timeout after 10 seconds
    setTimeout(() => {
      reject(new Error('Service worker message timeout'))
    }, 10000)
  })
}

/**
 * Force refresh all caches
 * This will clear and reinstall all cached assets
 */
export async function forceCacheRefresh() {
  try {
    const result = await sendMessageToSW({ type: 'FORCE_CACHE_REFRESH' })
    
    if (result.success) {
      console.log('Cache refresh completed successfully')
      return { success: true, message: 'Cache refreshed successfully' }
    } else {
      throw new Error(result.error || 'Cache refresh failed')
    }
  } catch (error) {
    console.error('Force cache refresh failed:', error)
    return { success: false, message: error.message }
  }
}

/**
 * Clear all caches completely
 * This will remove all cached data - use with caution
 */
export async function clearAllCaches() {
  try {
    const result = await sendMessageToSW({ type: 'CLEAR_ALL_CACHES' })
    
    if (result.success) {
      console.log('All caches cleared successfully')
      return { success: true, message: 'All caches cleared successfully' }
    } else {
      throw new Error(result.error || 'Clear caches failed')
    }
  } catch (error) {
    console.error('Clear all caches failed:', error)
    return { success: false, message: error.message }
  }
}

/**
 * Get cache information
 * Returns details about current cache state
 */
export async function getCacheInfo() {
  try {
    const result = await sendMessageToSW({ type: 'GET_CACHE_INFO' })
    
    return {
      success: true,
      version: result.version,
      staticAssets: result.staticAssets,
      dataAssets: result.dataAssets,
      totalSize: result.totalSize
    }
  } catch (error) {
    console.error('Get cache info failed:', error)
    return { success: false, message: error.message }
  }
}

/**
 * Force reload the application
 * This will reload the page and bypass cache
 */
export function forceReload() {
  if (window.location.search.includes('cache-bust')) {
    // Already tried cache bust, do hard reload
    window.location.reload(true)
  } else {
    // Add cache bust parameter and reload
    const url = new URL(window.location)
    url.searchParams.set('cache-bust', Date.now())
    window.location.href = url.toString()
  }
}

/**
 * Check for service worker updates
 * Returns true if an update is available
 */
export async function checkForUpdates() {
  if (!('serviceWorker' in navigator)) {
    return false
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration()
    if (!registration) {
      return false
    }

    // Check for updates
    await registration.update()
    
    // Check if there's a waiting service worker
    return !!registration.waiting
  } catch (error) {
    console.error('Check for updates failed:', error)
    return false
  }
}

/**
 * Activate waiting service worker
 * Forces activation of a new service worker if one is waiting
 */
export async function activateWaitingServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    return false
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration()
    if (!registration || !registration.waiting) {
      return false
    }

    // Send skip waiting message to the waiting service worker
    registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    
    return true
  } catch (error) {
    console.error('Activate waiting service worker failed:', error)
    return false
  }
}

/**
 * Browser cache management utilities
 */
export const browserCache = {
  /**
   * Clear browser storage (localStorage, sessionStorage, indexedDB)
   */
  async clearStorage() {
    try {
      // Clear localStorage
      localStorage.clear()
      
      // Clear sessionStorage
      sessionStorage.clear()
      
      // Clear indexedDB if available
      if ('indexedDB' in window) {
        const databases = await indexedDB.databases()
        await Promise.all(
          databases.map(db => {
            return new Promise((resolve, reject) => {
              const deleteReq = indexedDB.deleteDatabase(db.name)
              deleteReq.onsuccess = () => resolve()
              deleteReq.onerror = () => reject(deleteReq.error)
            })
          })
        )
      }
      
      return { success: true, message: 'Browser storage cleared' }
    } catch (error) {
      console.error('Clear browser storage failed:', error)
      return { success: false, message: error.message }
    }
  },

  /**
   * Get storage usage information
   */
  async getStorageInfo() {
    try {
      const info = {
        localStorage: 0,
        sessionStorage: 0,
        indexedDB: 0
      }

      // Calculate localStorage size
      let localStorageSize = 0
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          localStorageSize += localStorage[key].length + key.length
        }
      }
      info.localStorage = localStorageSize

      // Calculate sessionStorage size
      let sessionStorageSize = 0
      for (let key in sessionStorage) {
        if (sessionStorage.hasOwnProperty(key)) {
          sessionStorageSize += sessionStorage[key].length + key.length
        }
      }
      info.sessionStorage = sessionStorageSize

      // Get storage quota if available
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate()
        info.quota = estimate.quota
        info.usage = estimate.usage
      }

      return { success: true, info }
    } catch (error) {
      console.error('Get storage info failed:', error)
      return { success: false, message: error.message }
    }
  }
}

/**
 * Complete cache and storage reset
 * Nuclear option - clears everything and reloads
 */
export async function nuclearReset() {
  try {
    console.log('Starting nuclear reset...')
    
    // Clear all service worker caches
    await clearAllCaches()
    
    // Clear browser storage
    await browserCache.clearStorage()
    
    // Unregister service worker
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map(reg => reg.unregister()))
    }
    
    console.log('Nuclear reset complete, reloading...')
    
    // Force reload with cache bust
    forceReload()
    
    return { success: true, message: 'Complete reset performed' }
  } catch (error) {
    console.error('Nuclear reset failed:', error)
    return { success: false, message: error.message }
  }
} 